/**
 * API utility functions for making authenticated requests.
 */

// Use environment variable in production, fallback to localhost for development
export const API_BASE_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

// Admin token functions
export function getAdminToken(): string | null {
  return localStorage.getItem("adminAccessToken");
}

export function getAdminRefreshToken(): string | null {
  return localStorage.getItem("adminRefreshToken");
}

export function setAdminTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem("adminAccessToken", accessToken);
  localStorage.setItem("adminRefreshToken", refreshToken);
}

export function clearAdminTokens(): void {
  localStorage.removeItem("adminAccessToken");
  localStorage.removeItem("adminRefreshToken");
}

// Player token functions
export function getPlayerToken(): string | null {
  return localStorage.getItem("playerAccessToken");
}

export function getPlayerRefreshToken(): string | null {
  return localStorage.getItem("playerRefreshToken");
}

export function setPlayerTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem("playerAccessToken", accessToken);
  localStorage.setItem("playerRefreshToken", refreshToken);
}

export function clearPlayerTokens(): void {
  localStorage.removeItem("playerAccessToken");
  localStorage.removeItem("playerRefreshToken");
}

/**
 * Refresh the admin access token using the refresh token.
 * Returns the new access token or null if refresh fails.
 */
export async function refreshAdminAccessToken(): Promise<string | null> {
  const refreshToken = getAdminRefreshToken();
  if (!refreshToken) {
    return null;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/admins/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    const newAccessToken = data.access_token;
    
    // Update the stored access token
    localStorage.setItem("adminAccessToken", newAccessToken);
    
    return newAccessToken;
  } catch (error) {
    console.error("Failed to refresh admin token:", error);
    return null;
  }
}

/**
 * Refresh the player access token using the refresh token.
 * Returns the new access token or null if refresh fails.
 */
export async function refreshPlayerAccessToken(): Promise<string | null> {
  const refreshToken = getPlayerRefreshToken();
  if (!refreshToken) {
    return null;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/players/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    const newAccessToken = data.access_token;
    
    // Update the stored access token
    localStorage.setItem("playerAccessToken", newAccessToken);
    
    return newAccessToken;
  } catch (error) {
    console.error("Failed to refresh player token:", error);
    return null;
  }
}

/**
 * Make an authenticated API request for admins.
 * Automatically includes the Bearer token and handles token refresh if needed.
 */
export async function authenticatedFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = getAdminToken();
  
  if (!token) {
    throw new Error("No admin token found. Please log in.");
  }
  
  // Add Authorization header - ensure it's always set
  const headers = new Headers(options.headers || {});
  headers.set("Content-Type", "application/json");
  headers.set("Authorization", `Bearer ${token}`);

  let response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers,
  });
  console.log("fetch request", `${API_BASE_URL}${url}`, response.status)


  // If we get a 401, try to refresh the token and retry once
  if (response.status === 401 && token) {
    const newToken = await refreshAdminAccessToken();
    
    if (newToken) {
      // Retry the request with the new token
      const retryHeaders = new Headers(options.headers || {});
      retryHeaders.set("Content-Type", "application/json");
      retryHeaders.set("Authorization", `Bearer ${newToken}`);
      
      response = await fetch(`${API_BASE_URL}${url}`, {
        ...options,
        headers: retryHeaders,
      });
    } else {
      // Refresh failed, clear tokens
      clearAdminTokens();
      // Redirect to login would be handled by the app
    }
  }
  
  // If we get a 403, it might be a missing/invalid token issue
  if (response.status === 403) {
    console.error("403 Forbidden - check if admin token is valid");
  }

  return response;
}

/**
 * Make an authenticated API request for players.
 * Automatically includes the Bearer token and handles token refresh if needed.
 */
export async function authenticatedPlayerFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = getPlayerToken();
  
  // Add Authorization header
  const headers = {
    ...options.headers,
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  };

  let response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers,
  });

  // If we get a 401, try to refresh the token and retry once
  if (response.status === 401 && token) {
    const newToken = await refreshPlayerAccessToken();
    
    if (newToken) {
      // Retry the request with the new token
      response = await fetch(`${API_BASE_URL}${url}`, {
        ...options,
        headers: {
          ...headers,
          Authorization: `Bearer ${newToken}`,
        },
      });
    } else {
      // Refresh failed, clear tokens
      clearPlayerTokens();
      // Redirect to login would be handled by the app
    }
  }

  return response;
}

